import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getTables, createTable, updateTable, deleteTable, assignQrToTable,
  createQrCode, getTableDetail, deleteQrCode, getQrCodes,
  downloadQrCode, previewQrCode, getVenueAllItems, createTableOrder,
  updateOrderStatus, closeBill,
} from "@/api/endpoints";
import type { Order } from "@/types";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Plus, Trash2, QrCode, Users, Download, Copy, Check } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";

const STATUS_CONFIG = {
  empty: { labelKey: "table_empty", color: "bg-gray-100 text-gray-600" },
  occupied: { labelKey: "table_occupied", color: "bg-green-100 text-green-700" },
  waiting_bill: { labelKey: "table_waiting_bill", color: "bg-amber-100 text-amber-700" },
} as const;

function QrPreviewImg({ qrId }: { qrId: string }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let url = "";
    previewQrCode(qrId).then((blob: Blob) => { url = URL.createObjectURL(blob); setSrc(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [qrId]);
  if (!src) return <div className="flex size-32 items-center justify-center rounded-lg bg-muted"><QrCode className="size-6 text-muted-foreground" /></div>;
  return <img src={src} alt="QR" className="size-32 rounded-lg" />;
}

// ─── Main Page ───
export function TablesPage() {
  const { venueId } = useParams<{ venueId: string }>();
  const qc = useQueryClient();
  const { t } = useI18n();
  const { isStaff } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [detailId, setDetailId] = useState<string | null>(null);
  const [showAllQr, setShowAllQr] = useState(false);
  const [newOrderTables, setNewOrderTables] = useState<Set<string>>(new Set());
  const prevOrderCounts = useRef<Map<string, number>>(new Map());
  const isFirstLoad = useRef(true);

  const { data: tables, isLoading } = useQuery({
    queryKey: ["tables", venueId],
    queryFn: () => getTables(venueId!),
    enabled: !!venueId,
  });

  // Track new orders — only flag when activeOrders increases from previous
  useEffect(() => {
    if (!tables) return;

    if (isFirstLoad.current) {
      // First load: just store counts, don't flag anything
      const counts = new Map<string, number>();
      for (const table of tables) {
        counts.set(table.id || table._id!, table.activeOrders);
      }
      prevOrderCounts.current = counts;
      isFirstLoad.current = false;
      return;
    }

    setNewOrderTables(prev => {
      const next = new Set(prev);
      const newCounts = new Map<string, number>();
      for (const table of tables) {
        const id = table.id || table._id!;
        const prevCount = prevOrderCounts.current.get(id) || 0;
        newCounts.set(id, table.activeOrders);
        if (table.activeOrders > prevCount) {
          next.add(id);
        }
      }
      prevOrderCounts.current = newCounts;
      return next;
    });
  }, [tables]);

  const createMut = useMutation({
    mutationFn: () => createTable(venueId!, { name: newName }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tables", venueId] }); setCreateOpen(false); setNewName(""); },
  });

  const deleteMut = useMutation({
    mutationFn: deleteTable,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tables", venueId] }),
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => updateTable(id, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tables", venueId] }),
  });

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("tables")}</h1>
        {!isStaff && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowAllQr(true)}>
              <QrCode className="mr-2 size-4" />{t("all_qr_codes")}
            </Button>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 size-4" />{t("new_table")}
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-muted-foreground">{t("loading")}</div>
      ) : !tables?.length ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Users className="mb-4 size-12 text-muted-foreground" />
          <p className="text-muted-foreground">{t("no_tables")}</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {tables.map((table) => {
            const id = table.id || table._id!;
            const isEmpty = table.status === "empty" && table.activeOrders === 0;
            const isWaiting = table.status === "waiting_bill";
            const hasNewOrder = newOrderTables.has(id);
            const borderClass = isEmpty
              ? "border-border"
              : isWaiting
              ? "border-amber-400 bg-amber-50/50"
              : "border-primary bg-primary/5";
            return (
              <Card
                key={id}
                className={`cursor-pointer transition-all hover:shadow-md relative ${borderClass}`}
                onClick={() => {
                  setDetailId(id);
                  setNewOrderTables(prev => { const n = new Set(prev); n.delete(id); return n; });
                  qc.invalidateQueries({ queryKey: ["table-detail", id] });
                }}
              >
                {/* New order pulse */}
                {hasNewOrder && (
                  <div className="absolute -top-1.5 -right-1.5 flex items-center justify-center">
                    <span className="absolute size-4 rounded-full bg-red-500 animate-ping opacity-75" />
                    <span className="relative size-4 rounded-full bg-red-500 text-[8px] text-white font-bold flex items-center justify-center">{table.activeOrders}</span>
                  </div>
                )}
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className={`font-bold text-lg ${isEmpty ? "text-muted-foreground" : ""}`}>{table.name}</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    {(["empty", "occupied", "waiting_bill"] as const).map((s) => {
                      const active = table.status === s;
                      const cfg = STATUS_CONFIG[s];
                      return (
                        <button
                          key={s}
                          disabled={s === "empty" && table.activeOrders > 0}
                          className={`flex-1 rounded-md py-1.5 text-xs font-semibold transition-colors ${active ? cfg.color : "bg-muted/50 text-muted-foreground/60 hover:bg-muted"} ${s === "empty" && table.activeOrders > 0 ? "opacity-30 cursor-not-allowed" : ""}`}
                          onClick={(e) => { e.stopPropagation(); statusMut.mutate({ id, status: s }); }}
                        >
                          {t(cfg.labelKey as Parameters<typeof t>[0])}
                        </button>
                      );
                    })}
                  </div>
                  {table.totalOrders > 0 && (
                    <div className="mt-2 text-xs space-y-0.5">
                      {table.pendingCount > 0 && <p className="text-red-500">{table.pendingCount} {t("pending")}</p>}
                      {table.preparingCount > 0 && <p className="text-amber-600">{table.preparingCount} {t("preparing")}</p>}
                      {table.deliveredCount > 0 && <p className="text-green-600">{table.deliveredCount} {t("delivered")}</p>}
                      <div className="flex items-center justify-between pt-1">
                        <span className="text-muted-foreground">{t("total")} {table.totalOrders} {t("orders").toLowerCase()}</span>
                        <span className="font-bold text-foreground">{table.totalAmount.toFixed(0)} ₺</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create table dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("new_table")}</DialogTitle></DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); createMut.mutate(); }} className="space-y-4">
            <div>
              <Label>{t("table_name")}</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t("table_name_placeholder")} required />
            </div>
            <Button type="submit" className="w-full" disabled={createMut.isPending}>{createMut.isPending ? t("creating") : t("add")}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Table detail */}
      {detailId && <TableDetailDialog tableId={detailId} venueId={venueId!} onClose={() => setDetailId(null)} onDelete={() => { deleteMut.mutate(detailId); setDetailId(null); }} />}

      {/* All QR codes */}
      {showAllQr && <AllQrDialog venueId={venueId!} tables={tables || []} onClose={() => setShowAllQr(false)} />}
    </div>
  );
}

// ─── Table Detail Dialog ───
function TableDetailDialog({ tableId, venueId, onClose, onDelete }: {
  tableId: string; venueId: string; onClose: () => void; onDelete: () => void;
}) {
  const { t } = useI18n();
  const { isStaff } = useAuth();
  const qc = useQueryClient();
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [addItemsOpen, setAddItemsOpen] = useState(false);

  const statusMut = useMutation({
    mutationFn: ({ orderId, status }: { orderId: string; status: "pending" | "preparing" | "delivered" }) =>
      updateOrderStatus(orderId, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["table-detail", tableId] });
      qc.invalidateQueries({ queryKey: ["tables", venueId] });
    },
  });

  const closeBillMut = useMutation({
    mutationFn: () => closeBill(tableId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["table-detail", tableId] });
      qc.invalidateQueries({ queryKey: ["tables", venueId] });
      onClose();
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ["table-detail", tableId],
    queryFn: () => getTableDetail(tableId),
  });

  const table = data?.table;
  const orders = data?.orders || [];
  const allOrders = orders.filter((o: Order) => ["pending", "preparing", "delivered"].includes(o.status));
  const qr = table?.qrCodeId as unknown as { _id: string; shortUrl: string } | undefined;

  const statusMap: Record<string, { label: string; color: string }> = {
    pending: { label: t("pending"), color: "bg-amber-100 text-amber-700" },
    preparing: { label: t("preparing"), color: "bg-blue-100 text-blue-700" },
    delivered: { label: t("delivered"), color: "bg-green-100 text-green-700" },
  };

  return (
    <>
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        {!isStaff && (
          <button
            className="absolute right-10 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none"
            onClick={() => setQrDialogOpen(true)}
          >
            <QrCode className={`size-4 ${qr ? "text-primary" : "text-muted-foreground"}`} />
          </button>
        )}
        <DialogHeader>
          <DialogTitle>{table?.name || t("loading")}</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <p className="text-center text-muted-foreground py-8">{t("loading")}</p>
        ) : table ? (
          <div className="space-y-4">
            {/* Status + summary */}
            <div className="flex items-center gap-2">
              <Badge className={STATUS_CONFIG[table.status]?.color || ""}>{t((STATUS_CONFIG[table.status]?.labelKey || "table_empty") as Parameters<typeof t>[0])}</Badge>
              <span className="text-sm font-bold text-primary">{table.totalAmount.toFixed(0)} ₺</span>
            </div>

            {/* Add items button */}
            <Button className="w-full" onClick={() => setAddItemsOpen(true)}>
              <Plus className="mr-1.5 size-4" />{t("add_items_to_table")}
            </Button>

            {/* Active orders */}
            {allOrders.length > 0 ? (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t("orders")} ({allOrders.length})</p>
                <div className="space-y-2">
                  {allOrders.map((order: Order) => {
                    const oid = order.id || order._id!;
                    const steps: Array<"pending" | "preparing" | "delivered"> = ["pending", "preparing", "delivered"];
                    return (
                      <div key={oid} className="rounded-lg border p-3 text-sm">
                        <div className="flex justify-between items-center mb-2">
                          <div className="flex gap-1">
                            {steps.map((s) => {
                              const sm = statusMap[s] || statusMap.pending;
                              const active = order.status === s;
                              return (
                                <button
                                  key={s}
                                  className={`rounded-md px-2 py-0.5 text-[11px] font-semibold transition-colors ${active ? sm.color : "bg-muted/50 text-muted-foreground/50 hover:bg-muted"}`}
                                  onClick={() => statusMut.mutate({ orderId: oid, status: s })}
                                  disabled={statusMut.isPending}
                                >
                                  {sm.label}
                                </button>
                              );
                            })}
                          </div>
                          <span className="font-bold text-primary">{order.totalPrice} ₺</span>
                        </div>
                        <div className="space-y-0.5">
                          {order.items.map((item, i) => (
                            <div key={i} className="flex justify-between text-xs">
                              <span className="text-muted-foreground">{item.quantity}x {item.name}</span>
                              <span>{(item.price * item.quantity).toFixed(0)} ₺</span>
                            </div>
                          ))}
                        </div>
                        {order.note && <p className="mt-1 text-xs text-muted-foreground italic">{order.note}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">{t("no_orders")}</p>
            )}

            {/* Close bill */}
            {allOrders.length > 0 && (
              <ConfirmDialog
                trigger={<Button variant="default" className="w-full">{t("close_bill")}</Button>}
                title={t("close_bill")}
                description={t("close_bill_desc").replace("{{table}}", table.name).replace("{{amount}}", table.totalAmount.toFixed(0))}
                confirmLabel={t("close_bill_confirm")}
                onConfirm={() => closeBillMut.mutate()}
              />
            )}

            {/* Delete table */}
            <ConfirmDialog
              trigger={<Button variant="destructive" size="sm" className="w-full"><Trash2 className="mr-1 size-3.5" />{t("delete")}</Button>}
              title={t("delete")} description={`${table.name} silinecek.`} confirmLabel={t("delete")} onConfirm={onDelete}
            />
          </div>
        ) : null}
      </DialogContent>
    </Dialog>

    {/* QR popup */}
    {qrDialogOpen && <QrManageDialog tableId={tableId} venueId={venueId} tableName={table?.name || ""} qr={qr} onClose={() => setQrDialogOpen(false)} />}
    {/* Add Items popup */}
    {addItemsOpen && <AddItemsDialog tableId={tableId} venueId={venueId} onClose={() => setAddItemsOpen(false)} />}
    </>
  );
}

// ─── QR Manage Dialog (create/assign/view/delete) ───
function QrManageDialog({ tableId, venueId, tableName, qr, onClose }: {
  tableId: string; venueId: string; tableName: string; qr?: { _id: string; shortUrl: string }; onClose: () => void;
}) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [fgColor, setFgColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#FFFFFF");
  const [withLogo, setWithLogo] = useState(true);
  const [tab, setTab] = useState<"create" | "assign">("create");
  const [loading, setLoading] = useState(false);

  const { data: allQr } = useQuery({
    queryKey: ["qrCodes", venueId],
    queryFn: () => getQrCodes(venueId),
    enabled: !qr && tab === "assign",
  });

  const { data: tables } = useQuery({
    queryKey: ["tables", venueId],
    queryFn: () => getTables(venueId),
    enabled: !qr && tab === "assign",
  });

  const assignedQrIds = new Set((tables || []).map(t => {
    const q = t.qrCodeId as unknown as { _id?: string } | string | undefined;
    return q ? (typeof q === "string" ? q : q._id) : null;
  }).filter(Boolean));
  const unassignedQr = (allQr || []).filter(q => !assignedQrIds.has(q.id || (q as unknown as Record<string, string>)._id));

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["tables", venueId] });
    qc.invalidateQueries({ queryKey: ["table-detail", tableId] });
    qc.invalidateQueries({ queryKey: ["qrCodes", venueId] });
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      const newQr = await createQrCode(venueId, { label: tableName, style: (fgColor !== "#000000" || bgColor !== "#FFFFFF" || !withLogo) ? { fgColor, bgColor, withLogo } : undefined });
      await assignQrToTable(tableId, newQr.id || (newQr as unknown as Record<string, string>)._id);
      invalidateAll(); onClose();
    } catch { /* */ } finally { setLoading(false); }
  };

  const handleAssign = async (qrId: string) => {
    setLoading(true);
    try { await assignQrToTable(tableId, qrId); invalidateAll(); onClose(); } catch { /* */ } finally { setLoading(false); }
  };

  const handleDeleteQr = async () => {
    if (!qr) return;
    await assignQrToTable(tableId, null);
    await deleteQrCode(qr._id);
    invalidateAll(); onClose();
  };

  const handleDownload = async (format: "png" | "svg") => {
    if (!qr) return;
    const blob = await downloadQrCode(qr._id, format);
    const url = URL.createObjectURL(blob as Blob);
    const a = document.createElement("a"); a.href = url; a.download = `qr-${tableName}.${format}`; a.click(); URL.revokeObjectURL(url);
  };

  const handleCopy = () => {
    if (!qr) return;
    navigator.clipboard.writeText(`https://heremenu.app/q/${qr.shortUrl}`);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>QR — {tableName}</DialogTitle></DialogHeader>

        {qr ? (
          <div className="space-y-3">
            <div className="flex justify-center"><QrPreviewImg qrId={qr._id} /></div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1" onClick={() => handleDownload("png")}><Download className="mr-1 size-3.5" />PNG</Button>
              <Button size="sm" variant="outline" className="flex-1" onClick={() => handleDownload("svg")}><Download className="mr-1 size-3.5" />SVG</Button>
              <Button size="sm" variant="outline" className="flex-1" onClick={handleCopy}>
                {copied ? <><Check className="mr-1 size-3.5" />{t("copied")}</> : <><Copy className="mr-1 size-3.5" />{t("copy_url")}</>}
              </Button>
            </div>
            <ConfirmDialog
              trigger={<Button size="sm" variant="destructive" className="w-full"><Trash2 className="mr-1 size-3.5" />{t("delete_qr")}</Button>}
              title={t("delete_qr")} description={t("delete_qr_desc")} confirmLabel={t("delete")} onConfirm={handleDeleteQr}
            />
          </div>
        ) : createMode ? (
          <div className="space-y-3">
            <div className="flex gap-1 mb-2">
              <button className={`flex-1 py-1.5 rounded-lg text-xs font-medium ${tab === "create" ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`} onClick={() => setTab("create")}>{t("create_new_qr")}</button>
              <button className={`flex-1 py-1.5 rounded-lg text-xs font-medium ${tab === "assign" ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`} onClick={() => setTab("assign")}>{t("assign_existing_qr")}</button>
            </div>
            {tab === "create" ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">{t("dot_color")}</Label><div className="flex items-center gap-2 mt-1"><input type="color" value={fgColor} onChange={e => setFgColor(e.target.value)} className="size-8 rounded cursor-pointer" /><Input value={fgColor} onChange={e => setFgColor(e.target.value)} className="h-8 text-xs font-mono" /></div></div>
                  <div><Label className="text-xs">{t("bg")}</Label><div className="flex items-center gap-2 mt-1"><input type="color" value={bgColor} onChange={e => setBgColor(e.target.value)} className="size-8 rounded cursor-pointer" /><Input value={bgColor} onChange={e => setBgColor(e.target.value)} className="h-8 text-xs font-mono" /></div></div>
                </div>
                <div className="flex items-center gap-2"><Switch checked={withLogo} onCheckedChange={setWithLogo} /><Label className="text-xs">{t("show_logo_in_qr")}</Label></div>
                <Button className="w-full" onClick={handleCreate} disabled={loading}><QrCode className="mr-1.5 size-4" />{loading ? t("creating") : t("create_qr")}</Button>
              </div>
            ) : (
              <div className="space-y-2">
                {unassignedQr.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">{t("no_unassigned_qr")}</p>
                ) : unassignedQr.map(q => {
                  const id = q.id || (q as unknown as Record<string, string>)._id;
                  return (
                    <button key={id} className="flex w-full items-center gap-3 rounded-lg border p-3 hover:border-primary transition-colors text-left" onClick={() => handleAssign(id)} disabled={loading}>
                      <QrCode className="size-5 text-primary shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{t("unassigned")} {q.label && <span className="font-normal text-muted-foreground">({t("previously_assigned").replace("{{name}}", q.label)})</span>}</p>
                        <p className="text-xs text-muted-foreground">heremenu.app/q/{q.shortUrl}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-6">
            <QrCode className="mx-auto size-10 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground mb-4">{t("no_qr_assigned")}</p>
            <Button onClick={() => setCreateMode(true)}><Plus className="mr-1.5 size-4" />{t("create_or_assign_qr")}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── All QR Codes Dialog ───
function AllQrDialog({ venueId, tables, onClose }: {
  venueId: string; tables: Array<{ id?: string; _id?: string; name: string; qrCodeId?: unknown }>; onClose: () => void;
}) {
  const { t } = useI18n();
  const qc = useQueryClient();

  const { data: allQr, isLoading } = useQuery({
    queryKey: ["qrCodes", venueId],
    queryFn: () => getQrCodes(venueId),
  });

  const deleteMut = useMutation({
    mutationFn: deleteQrCode,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["qrCodes", venueId] }); qc.invalidateQueries({ queryKey: ["tables", venueId] }); },
  });

  // Map QR to table
  const qrToTable = new Map<string, string>();
  for (const table of tables) {
    const qrId = table.qrCodeId as unknown as { _id?: string } | string | undefined;
    if (!qrId) continue;
    const id = typeof qrId === "string" ? qrId : qrId._id;
    if (id) qrToTable.set(id, table.name);
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{t("all_qr_codes")}</DialogTitle></DialogHeader>
        {isLoading ? (
          <p className="text-center text-muted-foreground py-8">{t("loading")}</p>
        ) : !allQr?.length ? (
          <p className="text-center text-muted-foreground py-8">{t("no_qr")}</p>
        ) : (
          <div className="space-y-2">
            {allQr.map((qr) => {
              const id = qr.id || (qr as unknown as Record<string, string>)._id;
              const assignedTable = qrToTable.get(id);
              return (
                <div key={id} className="flex items-center gap-3 rounded-lg border p-3">
                  <QrCode className="size-5 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{qr.label || qr.shortUrl}</p>
                    <p className="text-xs text-muted-foreground">heremenu.app/q/{qr.shortUrl}</p>
                  </div>
                  {assignedTable ? (
                    <Badge variant="default" className="shrink-0">{assignedTable}</Badge>
                  ) : (
                    <Badge variant="secondary" className="shrink-0">{t("unassigned")}</Badge>
                  )}
                  <ConfirmDialog
                    trigger={<Button variant="ghost" size="icon" className="size-7 shrink-0"><Trash2 className="size-3.5 text-destructive" /></Button>}
                    title={t("delete_qr")} description={t("delete_qr_desc")} confirmLabel={t("delete")}
                    onConfirm={() => deleteMut.mutate(id)}
                  />
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Add Items Dialog ───
function AddItemsDialog({ tableId, venueId, onClose }: {
  tableId: string; venueId: string; onClose: () => void;
}) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [cart, setCart] = useState<Map<string, { name: string; price: number; quantity: number }>>(new Map());
  const [search, setSearch] = useState("");

  const { data: categories } = useQuery({
    queryKey: ["all-items", venueId],
    queryFn: () => getVenueAllItems(venueId),
  });

  const orderMut = useMutation({
    mutationFn: () => createTableOrder(tableId, {
      items: Array.from(cart.entries()).map(([itemId, { quantity }]) => ({ itemId, quantity })),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["table-detail", tableId] });
      qc.invalidateQueries({ queryKey: ["tables", venueId] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      onClose();
    },
  });

  const addItem = (id: string, name: string, price: number) => {
    setCart(prev => {
      const next = new Map(prev);
      const existing = next.get(id);
      next.set(id, { name, price, quantity: (existing?.quantity || 0) + 1 });
      return next;
    });
  };

  const removeItem = (id: string) => {
    setCart(prev => {
      const next = new Map(prev);
      const existing = next.get(id);
      if (existing && existing.quantity > 1) {
        next.set(id, { ...existing, quantity: existing.quantity - 1 });
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const totalPrice = Array.from(cart.values()).reduce((s, i) => s + i.price * i.quantity, 0);
  const totalItems = Array.from(cart.values()).reduce((s, i) => s + i.quantity, 0);

  const filteredCategories = categories?.map(cat => ({
    ...cat,
    items: cat.items.filter(i => !search || i.name.toLowerCase().includes(search.toLowerCase())),
  })).filter(c => c.items.length > 0);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" style={{ height: "80vh", display: "flex", flexDirection: "column" }}>
        <DialogHeader>
          <DialogTitle>{t("add_items_to_table")}</DialogTitle>
        </DialogHeader>

        <Input placeholder={t("search")} value={search} onChange={(e) => setSearch(e.target.value)} />

        <div className="flex-1 overflow-y-auto min-h-0 mt-2">
          {filteredCategories?.map(cat => (
            <div key={cat._id} className="mb-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 sticky top-0 bg-background py-1 z-10">{cat.name}</p>
              <div className="grid grid-cols-2 gap-2">
                {cat.items.map(item => {
                  const inCart = cart.get(item._id);
                  return (
                    <div
                      key={item._id}
                      className={`rounded-xl border p-3 cursor-pointer transition-all ${inCart ? "border-primary bg-primary/5" : "hover:border-muted-foreground/30"}`}
                      onClick={() => addItem(item._id, item.name, item.price)}
                    >
                      {item.image && <img src={item.image} alt="" className="w-full h-20 rounded-lg object-cover mb-2" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />}
                      <p className="text-sm font-semibold truncate">{item.name}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-sm font-bold text-primary">{item.price} ₺</span>
                        {inCart && (
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <button className="size-6 rounded-full bg-muted flex items-center justify-center text-xs font-bold" onClick={() => removeItem(item._id)}>−</button>
                            <span className="w-5 text-center text-sm font-bold">{inCart.quantity}</span>
                            <button className="size-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold" onClick={() => addItem(item._id, item.name, item.price)}>+</button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Fixed bottom cart */}
        <div className="border-t pt-3">
          {totalItems > 0 ? (
            <>
              <div className="flex items-center justify-between mb-2 text-sm">
                <span className="text-muted-foreground">{totalItems} {t("items_count")}</span>
                <span className="text-lg font-bold text-primary">{totalPrice.toFixed(0)} ₺</span>
              </div>
              <Button className="w-full" onClick={() => orderMut.mutate()} disabled={orderMut.isPending}>
                {orderMut.isPending ? t("creating") : t("create_order")}
              </Button>
            </>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-1">{t("search")}...</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
